import React, { useState } from 'react';

/**
 * 공용 버튼. 사용법은 docs/ui-components.md 참고.
 *
 * variant 고르는 법
 * - primary   : 화면의 가장 중요한 행동 (주황)
 * - submit    : 다이얼로그·폼의 저장/확인 (남색)
 * - outline   : 주황 테두리의 보조 행동
 * - cancel    : 취소·닫기 (회색 채움)
 * - line      : 흰 바탕 + 회색 테두리의 가벼운 행동 (편집, 더보기 등)
 * - secondary : 연회색 바탕의 보조 행동
 * - danger    : 삭제·거절처럼 되돌리기 어려운 행동 (빨강 채움)
 * - dangerOutline : 빨강 테두리의 위험한 보조 행동 (경매 취소 등)
 * - default, edit : 예전 이름. 새 코드에서는 쓰지 않는다 (default → primary, edit → line).
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'cancel'
  | 'submit'
  | 'line'
  | 'danger'
  | 'dangerOutline'
  /** @deprecated primary를 쓴다. */
  | 'default'
  /** @deprecated line을 쓴다. */
  | 'edit';

export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent) => void;
  disabled?: boolean;
  width?: string;
  height?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  type?: 'button' | 'submit' | 'reset';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  iconSize?: number;
  padding?: string;
  style?: React.CSSProperties;
  /** 글자 없이 아이콘만 있을 때 화면 낭독기가 읽을 이름 */
  ariaLabel?: string;
  title?: string;
}

const SIZE_STYLES: Record<ButtonSize, { height: string; fontSize: string; padding: string; lineHeight: string }> = {
  small: { height: '32px', fontSize: '12px', padding: '6px 12px', lineHeight: '18px' },
  medium: { height: '40px', fontSize: '14px', padding: '8px 16px', lineHeight: '21px' },
  large: { height: '48px', fontSize: '16px', padding: '12px 24px', lineHeight: '24px' },
};

type VariantStyle = { base: React.CSSProperties; hover: React.CSSProperties };

const BRAND = 'var(--Color-fill-brand-color, #FF6F0F)';
const NEGATIVE = 'var(--Color-status-negative, #FF3838)';

const VARIANT_STYLES: Record<Exclude<ButtonVariant, 'default' | 'edit'>, VariantStyle> = {
  primary: {
    base: { backgroundColor: BRAND, color: 'var(--Color-text-inverse, white)', border: 'none' },
    hover: { backgroundColor: 'var(--Color-primary-strong, #E8650E)' },
  },
  secondary: {
    base: {
      backgroundColor: 'var(--Color-background-sub, #F8F8F8)',
      color: 'var(--Color-text-normal, #161616)',
      border: '1px solid var(--Color-line-normal, #F0F0F0)',
    },
    hover: { backgroundColor: 'var(--Color-line-normal, #F0F0F0)' },
  },
  outline: {
    base: { backgroundColor: 'transparent', color: BRAND, border: `1px solid ${BRAND}` },
    hover: { backgroundColor: BRAND, color: 'var(--Color-text-inverse, white)' },
  },
  cancel: {
    base: {
      backgroundColor: 'var(--Color-fill-sub, #E9E9E9)',
      color: 'var(--Color-text-normal, #161616)',
      border: '1px solid var(--Color-line-normal, #F0F0F0)',
    },
    hover: { filter: 'brightness(0.95)' },
  },
  submit: {
    base: { backgroundColor: 'var(--Color-fill-normal, #293044)', color: 'var(--Color-text-inverse, white)', border: 'none' },
    hover: { filter: 'brightness(1.25)' },
  },
  danger: {
    base: { backgroundColor: NEGATIVE, color: 'var(--Color-text-inverse, white)', border: 'none' },
    hover: { filter: 'brightness(0.92)' },
  },
  dangerOutline: {
    base: { backgroundColor: 'var(--Color-background-normal, white)', color: NEGATIVE, border: `1px solid ${NEGATIVE}` },
    hover: { backgroundColor: NEGATIVE, color: 'var(--Color-text-inverse, white)' },
  },
  line: {
    base: {
      backgroundColor: 'var(--Color-background-normal, white)',
      color: 'var(--Color-text-normal, #161616)',
      border: '1px solid var(--Color-line-normal, #F0F0F0)',
    },
    hover: { backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' },
  },
};

const DISABLED_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--Color-fill-inactive, #F5F5F5)',
  color: 'var(--Color-text-assistive, #989898)',
  border: '1px solid var(--Color-line-normal, #F0F0F0)',
  cursor: 'not-allowed',
  opacity: 0.6,
};

const FOCUS_RING = '0 0 0 2px var(--Color-background-normal, white), 0 0 0 4px var(--Color-fill-brand-color, #FF6F0F)';

/** 키보드로 포커스했을 때만 테두리를 보여 준다. :focus-visible을 모르는 옛 브라우저에서는 보여 주지 않는다. */
const isKeyboardFocus = (element: HTMLElement) => {
  try {
    return element.matches(':focus-visible');
  } catch {
    return false;
  }
};

const getVariantStyle =(variant: ButtonVariant): VariantStyle => {
  if (variant === 'default') return VARIANT_STYLES.primary;
  if (variant === 'edit') {
    // 예전 edit: line과 같고 글씨만 조금 옅다.
    return {
      base: { ...VARIANT_STYLES.line.base, color: 'var(--Color-text-assistive-strong, #4C4C4C)' },
      hover: VARIANT_STYLES.line.hover,
    };
  }
  return VARIANT_STYLES[variant];
};

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled = false,
  width,
  height,
  size = 'medium',
  variant = 'primary',
  type = 'button',
  leftIcon,
  rightIcon,
  iconSize = 20,
  padding,
  style,
  ariaLabel,
  title,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusVisible, setIsFocusVisible] = useState(false);

  const sizeStyle = SIZE_STYLES[size];
  const variantStyle = getVariantStyle(variant);
  const stateStyle: React.CSSProperties = {
    ...(disabled ? DISABLED_STYLE : variantStyle.base),
    ...(!disabled && isHovered ? variantStyle.hover : null),
  };
  // 호출하는 쪽이 style.background(축약형)를 주면 backgroundColor를 함께 쓰지 않는다.
  // 둘을 섞으면 hover·disabled로 다시 그릴 때 backgroundColor가 background를 덮어 버린다.
  if (style?.background !== undefined) {
    delete stateStyle.backgroundColor;
  }

  const buttonStyle: React.CSSProperties = {
    width: width || 'auto',
    height: height || sizeStyle.height,
    padding: padding || sizeStyle.padding,
    borderRadius: '8px',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'inline-flex',
    gap: '8px',
    fontSize: sizeStyle.fontSize,
    fontWeight: '500',
    lineHeight: sizeStyle.lineHeight,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    cursor: 'pointer',
    opacity: 1,
    transition: 'background-color 0.2s ease, color 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease',
    outline: 'none',
    boxSizing: 'border-box',
    margin: 0,
    ...stateStyle,
    ...(isFocusVisible ? { boxShadow: FOCUS_RING } : null),
    // 호출하는 쪽 style이 마지막에 이긴다 (배경색을 직접 준 경우 hover 색으로 덮지 않음).
    ...style,
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // type="submit"이면 onClick 뒤에 form의 onSubmit이 그대로 실행된다.
    onClick?.(e);
  };

  const iconBox = (icon: React.ReactNode, justify: 'flex-start' | 'flex-end') => (
    <span
      aria-hidden="true"
      style={{
        width: `${iconSize}px`,
        height: `${iconSize}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: justify,
        flexShrink: 0,
      }}
    >
      {icon}
    </span>
  );

  return (
    <button
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={(e) => setIsFocusVisible(isKeyboardFocus(e.currentTarget))}
      onBlur={() => setIsFocusVisible(false)}
      style={buttonStyle}
    >
      {leftIcon && iconBox(leftIcon, 'flex-start')}
      <span>{children}</span>
      {rightIcon && iconBox(rightIcon, 'flex-end')}
    </button>
  );
};

export default Button;
